/** Conversões de cor, luminância e contraste (WCAG 2.1). */
export function limitar(valor, min, max) {
    return Math.min(max, Math.max(min, valor));
}
export function arredondar(valor, casas = 0) {
    const fator = 10 ** casas;
    return Math.round(valor * fator) / fator;
}
/** Normaliza qualquer entrada para `#rrggbb` em minúsculas. */
export function normalizarHex(hex) {
    let valor = (hex ?? '').trim().replace(/^#/, '');
    if (valor.length === 3) {
        valor = valor
            .split('')
            .map((c) => c + c)
            .join('');
    }
    if (!/^[0-9a-fA-F]{6}$/.test(valor))
        return '#888888';
    return `#${valor.toLowerCase()}`;
}
export function hexParaRgb(hex) {
    const valor = normalizarHex(hex).slice(1);
    return {
        r: parseInt(valor.slice(0, 2), 16),
        g: parseInt(valor.slice(2, 4), 16),
        b: parseInt(valor.slice(4, 6), 16),
    };
}
export function rgbParaHex({ r, g, b }) {
    const paraHex = (n) => limitar(Math.round(n), 0, 255).toString(16).padStart(2, '0');
    return `#${paraHex(r)}${paraHex(g)}${paraHex(b)}`;
}
export function rgbParaHsl({ r, g, b }) {
    const rr = r / 255;
    const gg = g / 255;
    const bb = b / 255;
    const max = Math.max(rr, gg, bb);
    const min = Math.min(rr, gg, bb);
    const delta = max - min;
    let h = 0;
    if (delta !== 0) {
        if (max === rr)
            h = ((gg - bb) / delta) % 6;
        else if (max === gg)
            h = (bb - rr) / delta + 2;
        else
            h = (rr - gg) / delta + 4;
        h *= 60;
        if (h < 0)
            h += 360;
    }
    const l = (max + min) / 2;
    const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
    return { h: arredondar(h, 2), s: arredondar(s * 100, 2), l: arredondar(l * 100, 2) };
}
export function hslParaRgb({ h, s, l }) {
    const hh = ((h % 360) + 360) % 360;
    const ss = limitar(s, 0, 100) / 100;
    const ll = limitar(l, 0, 100) / 100;
    const c = (1 - Math.abs(2 * ll - 1)) * ss;
    const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
    const m = ll - c / 2;
    let rp = 0;
    let gp = 0;
    let bp = 0;
    if (hh < 60)
        [rp, gp, bp] = [c, x, 0];
    else if (hh < 120)
        [rp, gp, bp] = [x, c, 0];
    else if (hh < 180)
        [rp, gp, bp] = [0, c, x];
    else if (hh < 240)
        [rp, gp, bp] = [0, x, c];
    else if (hh < 300)
        [rp, gp, bp] = [x, 0, c];
    else
        [rp, gp, bp] = [c, 0, x];
    return {
        r: Math.round((rp + m) * 255),
        g: Math.round((gp + m) * 255),
        b: Math.round((bp + m) * 255),
    };
}
export function hexParaHsl(hex) {
    return rgbParaHsl(hexParaRgb(hex));
}
export function hslParaHex(hsl) {
    return rgbParaHex(hslParaRgb(hsl));
}
/** Luminância relativa conforme WCAG. */
export function luminanciaRelativa(hex) {
    const { r, g, b } = hexParaRgb(hex);
    const canal = (valor) => {
        const c = valor / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b);
}
/** Razão de contraste entre duas cores (1 a 21). */
export function razaoContraste(corA, corB) {
    const a = luminanciaRelativa(corA);
    const b = luminanciaRelativa(corB);
    const claro = Math.max(a, b);
    const escuro = Math.min(a, b);
    return arredondar((claro + 0.05) / (escuro + 0.05), 2);
}
export function escurecer(hex, pontos) {
    const hsl = hexParaHsl(hex);
    return hslParaHex({ ...hsl, l: limitar(hsl.l - pontos, 0, 100) });
}
export function clarear(hex, pontos) {
    const hsl = hexParaHsl(hex);
    return hslParaHex({ ...hsl, l: limitar(hsl.l + pontos, 0, 100) });
}
export function saturar(hex, pontos) {
    const hsl = hexParaHsl(hex);
    return hslParaHex({ ...hsl, s: limitar(hsl.s + pontos, 0, 100) });
}
export function girarMatiz(hex, graus) {
    const hsl = hexParaHsl(hex);
    return hslParaHex({ ...hsl, h: (hsl.h + graus + 360) % 360 });
}
/** Mistura linear entre duas cores (0 = A, 1 = B). */
export function misturar(corA, corB, peso) {
    const t = limitar(peso, 0, 1);
    const a = hexParaRgb(corA);
    const b = hexParaRgb(corB);
    return rgbParaHex({
        r: a.r + (b.r - a.r) * t,
        g: a.g + (b.g - a.g) * t,
        b: a.b + (b.b - a.b) * t,
    });
}
/** Constrói um `rgba()` a partir de hex + alfa (0-1). */
export function comAlfa(hex, alfa) {
    const { r, g, b } = hexParaRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${limitar(alfa, 0, 1)})`;
}
/**
 * Escurece (ou clareia) uma cor até atingir a razão de contraste desejada
 * contra um fundo. É o que garante texto legível no papel.
 */
export function garantirContraste(texto, fundo, alvo = 7) {
    const claro = luminanciaRelativa(fundo) < 0.5 ? false : true;
    let atual = texto;
    if (razaoContraste(atual, fundo) >= alvo)
        return atual;
    for (let passo = 0; passo < 60; passo += 1) {
        const hsl = hexParaHsl(atual);
        const nova = claro
            ? { ...hsl, l: limitar(hsl.l - 2, 0, 100) }
            : { ...hsl, l: limitar(hsl.l + 2, 0, 100) };
        atual = hslParaHex(nova);
        if (razaoContraste(atual, fundo) >= alvo)
            return atual;
    }
    return atual;
}
//# sourceMappingURL=convert.js.map